import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { buildApiUrl, WEBSOCKET_URL } from "./config";

// Add CSS for gradient animation
const styles = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const ParentChannelChat = ({ 
  parentId, 
  parentName, 
  teacherId, 
  teacherName, 
  teacherEmail, 
  studentId, 
  studentName, 
  channelId 
}) => {
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Translation states
  const [translatedMessages, setTranslatedMessages] = useState({}); // {messageId: {content, language}}
  const [translatingMessages, setTranslatingMessages] = useState(new Set()); // Track loading state

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Scroll the messages container to bottom without affecting page scroll
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Translate message content
  const translateMessage = async (message, targetLang = 'ar') => {
    const messageId = message.id;
    const messageContent = message.message || message.content;
    
    // If already translated to this language, toggle back to original
    if (translatedMessages[messageId]?.language === targetLang) {
      setTranslatedMessages(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
      return;
    }

    try {
      setTranslatingMessages(prev => new Set([...prev, messageId]));
      
      // Use Google Translate API (free tier)
      const translateText = async (text, target) => {
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${target}&dt=t&q=${encodeURIComponent(text)}`;
        const response = await fetch(url);
        const data = await response.json();
        return data[0].map(item => item[0]).join('');
      };

      const translatedContent = await translateText(messageContent, targetLang);
      
      setTranslatedMessages(prev => ({
        ...prev,
        [messageId]: {
          content: translatedContent,
          language: targetLang,
          languageName: targetLang === 'ar' ? 'Arabic' : 'English'
        }
      }));
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setTranslatingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  // Load conversation messages
  const loadMessages = async () => {
    try {
      setLoading(true);
      console.log("📥 Loading messages for:", { studentId, parentId, teacherEmail });
      
      // Use the enhanced API endpoint that includes names
      const response = await axios.get(
        buildApiUrl(`/messages/thread/${studentId}/${parentId}/${teacherEmail}`)
      );
      
      if (response.data) {
        console.log("✅ Loaded messages:", response.data);
        setMessages(response.data);
      }
    } catch (error) {
      console.error("❌ Failed to load messages:", error);
      // Try fallback endpoint if needed
      try {
        const response = await fetch(
          buildApiUrl(`/api/chat/messages/${parentId}/${teacherEmail}`)
        );
        if (response.ok) {
          const data = await response.json();
          setMessages(data.messages || []);
        }
      } catch (fallbackError) {
        console.error("❌ Fallback also failed:", fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  // Connect to WebSocket
  const connectWebSocket = () => {
    if (ws) {
      ws.close();
    }

    const wsBase = buildApiUrl('').replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
    const wsUrl = `${wsBase}/ws/chat/${encodeURIComponent(parentId)}`;
    console.log("Connecting to WebSocket:", wsUrl);
    
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log("✅ WebSocket connected for channel:", channelId);
      setConnected(true);
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("📨 WebSocket message received:", data);
      
      if (data.type === "message_sent") {
        // Message sent confirmation
        console.log("✅ Message sent confirmation");
      } else if (data.sender && data.receiver) {
        // New incoming message - only add if it's for this conversation
        if (
          (data.sender === teacherEmail && data.receiver === parentId) ||
          (data.sender === parentId && data.receiver === teacherEmail)
        ) {
          setMessages(prev => [...prev, data]);
          scrollToBottom();
        }
      }
    };
    
    websocket.onclose = () => {
      console.log("❌ WebSocket disconnected");
      setConnected(false);
    };
    
    websocket.onerror = (error) => {
      console.error("❌ WebSocket error:", error);
    };
    
    setWs(websocket);
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim()) {
      return;
    }

    try {
      // Create enhanced message payload
      const messagePayload = {
        student_id: studentId,
        message: newMessage,
        sender_id: parentId,
        sender_role: "parent",
        receiver_id: teacherEmail,
        receiver_role: "teacher",
        sender_name: parentName,
        receiver_name: teacherName
      };

      console.log("📤 Sending message via API:", messagePayload);
      
      // Send via REST API for persistence
      const response = await axios.post(buildApiUrl("/messages/"), messagePayload);
      
      if (response.data) {
        console.log("✅ Message sent successfully:", response.data);
        
        // Add the returned message to local state
        setMessages(prev => [...prev, response.data]);
        setNewMessage("");
        scrollToBottom();
        
        // Also send via WebSocket for real-time delivery if connected
        if (ws && connected) {
          const wsData = {
            to: teacherEmail,
            content: newMessage,
            type: "text",
            channel_id: channelId,
            student_id: studentId,
            message_id: response.data.id
          };
          ws.send(JSON.stringify(wsData));
        }
      }
    } catch (error) {
      console.error("❌ Failed to send message:", error);
      
      // Fallback: add optimistic message if API fails but WebSocket works
      if (ws && connected) {
        const messageData = {
          to: teacherEmail,
          content: newMessage,
          type: "text",
          channel_id: channelId,
          student_id: studentId
        };
        ws.send(JSON.stringify(messageData));
        
        const optimisticMessage = {
          id: Date.now().toString(),
          sender_id: parentId,
          receiver_id: teacherEmail,
          sender_name: parentName,
          receiver_name: teacherName,
          content: newMessage,
          timestamp: new Date().toISOString(),
          type: "text",
          read: false
        };
        
        setMessages(prev => [...prev, optimisticMessage]);
        setNewMessage("");
        scrollToBottom();
      }
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    // Validate each file size (10MB limit)
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }
    }

    setUploading(true);
    
    try {
      // Upload each file separately
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sender_id', parentId);
        formData.append('receiver_id', teacherEmail);
        formData.append('sender_name', parentName);
        formData.append('receiver_name', teacherName);
        formData.append('student_id', studentId);
        formData.append('sender_role', 'parent');
        formData.append('receiver_role', 'teacher');

        console.log("📎 Parent uploading file:", file.name);
        
        const response = await axios.post(buildApiUrl('/api/chat/upload'), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data) {
          console.log("✅ File uploaded successfully:", response.data);
        }
      }
      
      // Force refresh messages to show all new attachments
      try {
        const endpoint = buildApiUrl(`/messages/thread/${studentId}/${parentId}/${teacherEmail}`);
        const messagesRes = await axios.get(endpoint + `?t=${Date.now()}`);
        if (messagesRes.data) {
          console.log("📥 Refreshed messages after upload:", messagesRes.data);
          setMessages(messagesRes.data);
        }
      } catch (refreshError) {
        console.error("❌ Failed to refresh messages after upload:", refreshError);
      }
      
      scrollToBottom();
    } catch (error) {
      console.error("❌ File upload failed:", error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Delete message for everyone
  const deleteMessage = async (messageId) => {
    if (!confirm('Delete this message for everyone? This cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(
        buildApiUrl(`/messages/${messageId}`),
        {
          params: { sender_id: parentId }
        }
      );

      if (response.data.success) {
        // Remove message from local state
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        console.log("✅ Message deleted successfully");
      }
    } catch (error) {
      console.error("❌ Failed to delete message:", error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for grouping
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    
    messages.forEach(message => {
      const messageDate = new Date(message.timestamp).toDateString();
      if (!groups[messageDate]) {
        groups[messageDate] = [];
      }
      groups[messageDate].push(message);
    });
    
    return Object.keys(groups)
      .sort((a, b) => new Date(a) - new Date(b))
      .map(date => ({
        date,
        messages: groups[date]
      }));
  };

  // Initialize component
  useEffect(() => {
    if (parentId && teacherEmail) {
      loadMessages();
      connectWebSocket();
      
      // Add polling every 1 second for reliable message updates
      let cancelled = false;
      const pollMessages = async () => {
        if (cancelled) return;
        try {
          const response = await axios.get(
            buildApiUrl(`/messages/thread/${studentId}/${parentId}/${teacherEmail}?t=${Date.now()}`)
          );
          if (!cancelled && response.data) {
            console.log(`📩 Polled ${response.data.length} messages for parent channel ${channelId}`);
            setMessages(response.data);
          }
        } catch (error) {
          console.error("❌ Polling error:", error);
        }
      };
      
      const pollInterval = setInterval(pollMessages, 1000);

      return () => {
        cancelled = true;
        clearInterval(pollInterval);
        if (ws) {
          ws.close();
        }
      };
    }
  }, [channelId, parentId, teacherEmail, studentId]);

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inject CSS styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-hidden">
      {/* Messages Area - Mobile Optimized */}
      <div 
        ref={messagesContainerRef} 
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-2"
        style={{ 
          minHeight: 0,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaf6 25%, #f3e5f5 50%, #fce4ec 75%, #f5f7fa 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient 15s ease infinite',
          backgroundImage: `linear-gradient(135deg, #f5f7fa 0%, #e8eaf6 25%, #f3e5f5 50%, #fce4ec 75%, #f5f7fa 100%), 
                            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill-opacity='0.03'%3E%3Ccircle fill='%239C27B0' cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-gray-500 text-sm">Loading messages...</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 py-2">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <div className="bg-white rounded-lg p-6 shadow-sm mx-auto max-w-sm">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium mb-1">No messages yet</p>
                  <p className="text-sm text-gray-500">Start chatting with {teacherName}</p>
                </div>
              </div>
            ) : (
              groupMessagesByDate(messages).map((group) => (
                <div key={group.date} className="space-y-2">
                  {/* Date separator */}
                  <div className="flex justify-center my-4">
                    <div className="bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-1 rounded-full text-xs font-medium shadow-sm">
                      {formatDate(new Date(group.date))}
                    </div>
                  </div>
                  
                  {/* Messages for this date */}
                  {group.messages.map((message, index) => {
                    const isParent = message.sender_id === parentId || message.sender === parentId;
                    const messageContent = message.message || message.content;
                    
                    return (
                      <div key={message.id || index} className={`flex items-end space-x-2 ${isParent ? 'justify-end' : 'justify-start'}`}>
                        {/* Avatar for teacher messages (left side) */}
                        {!isParent && (
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold mb-1 flex-shrink-0">
                            {(teacherName?.charAt(0) || 'T').toUpperCase()}
                          </div>
                        )}
                        
                        {/* Message Bubble - Mobile Optimized */}
                        <div className={`max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl ${isParent ? 'ml-12' : 'mr-12'}`}>
                          <div className={`group rounded-2xl px-4 py-2 shadow-sm relative ${
                            isParent 
                              ? 'bg-green-500 text-white rounded-br-md' 
                              : 'bg-white text-gray-900 rounded-bl-md'
                          }`}>
                            {/* Message tail */}
                            <div className={`absolute bottom-0 ${
                              isParent 
                                ? 'right-0 transform translate-x-1 w-0 h-0 border-l-8 border-l-green-500 border-b-8 border-b-transparent' 
                                : 'left-0 transform -translate-x-1 w-0 h-0 border-r-8 border-r-white border-b-8 border-b-transparent'
                            }`}></div>
                            
                            {/* Message Content */}
                            {/* Delete button for parent's own messages */}
                            {isParent && (
                              <button
                                onClick={() => deleteMessage(message.id)}
                                className="absolute -right-8 top-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                                title="Delete for everyone"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                            
                            {message.attachment_url ? (
                              <div className="space-y-2">
                                {/* Show message text if any */}
                                {messageContent && messageContent !== `📎 ${message.attachment_filename}` && (
                                  <p className="text-sm leading-relaxed break-words">{messageContent}</p>
                                )}
                                
                                {/* Attachment Display */}
                                {message.attachment_filename && /\.(jpg|jpeg|png|gif|webp)$/i.test(message.attachment_filename) ? (
                                  <a href={buildApiUrl(message.attachment_url)} target="_blank" rel="noopener noreferrer">
                                    <img 
                                      src={buildApiUrl(message.attachment_url)} 
                                      alt={message.attachment_filename} 
                                      className="max-w-xs rounded-lg cursor-pointer"
                                      style={{ maxHeight: 200 }}
                                    />
                                  </a>
                                ) : (
                                  <a 
                                    href={buildApiUrl(message.attachment_url)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    download={message.attachment_filename}
                                    className={`inline-flex items-center space-x-2 p-2 rounded-lg ${
                                      isParent ? 'bg-green-400 text-white' : 'bg-gray-100 text-gray-900'
                                    } hover:opacity-80`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <span className="text-sm font-medium">{message.attachment_filename}</span>
                                    {message.attachment_size && (
                                      <span className="text-xs opacity-75">
                                        ({(message.attachment_size / 1024 / 1024).toFixed(2)} MB)
                                      </span>
                                    )}
                                  </a>
                                )}
                              </div>
                            ) : (
                              <div>
                                {translatedMessages[message.id] ? (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1 mb-1">
                                      <svg className="w-3 h-3 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                      </svg>
                                      <span className="text-xs opacity-70">{translatedMessages[message.id].languageName}</span>
                                    </div>
                                    <p className="text-sm leading-relaxed break-words" style={{ direction: translatedMessages[message.id].language === 'ar' ? 'rtl' : 'ltr' }}>
                                      {translatedMessages[message.id].content}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-sm leading-relaxed break-words">{messageContent}</p>
                                )}
                              </div>
                            )}
                            <div className="flex justify-between items-center mt-1">
                              <button
                                onClick={() => translateMessage(message, translatedMessages[message.id]?.language === 'ar' ? 'en' : 'ar')}
                                disabled={translatingMessages.has(message.id)}
                                className={`opacity-60 hover:opacity-100 transition-opacity p-1 rounded ${isParent ? 'hover:bg-green-400' : 'hover:bg-gray-100'}`}
                                title={translatedMessages[message.id] ? 'Show original' : 'Translate'}
                              >
                                {translatingMessages.has(message.id) ? (
                                  <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                  </svg>
                                )}
                              </button>
                              <div className="flex items-center space-x-2">
                                <span className={`text-xs ${
                                  isParent ? 'text-green-100' : 'text-gray-500'
                                }`}>
                                  {formatTime(message.timestamp)}
                                </span>
                                
                                {/* Delete button for sent messages */}
                                {isParent && (
                                  <button
                                    onClick={() => deleteMessage(message.id)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-green-400 rounded-full"
                                    title="Delete for everyone"
                                  >
                                    <svg className="w-3 h-3 text-green-100" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                                
                                {isParent && message.read !== undefined && (
                                  <div className="text-xs text-green-100">
                                    {message.read ? (
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                                        <path d="M12.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L4 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                                      </svg>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Avatar for parent messages (right side) */}
                        {isParent && (
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-semibold mb-1 flex-shrink-0">
                            {(parentName?.charAt(0) || 'P').toUpperCase()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input - WhatsApp Style Mobile Optimized */}
      <div className="bg-gray-200 p-2" style={{ flexShrink: 0 }}>
        <div className="flex items-end space-x-2">
          {/* Attachment Button */}
          <button 
            onClick={triggerFileInput}
            disabled={uploading}
            className={`p-2 transition-colors rounded-full ${
              uploading 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-300'
            }`}
          >
            {uploading ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            )}
          </button>
          
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            accept="image/*,application/pdf,.doc,.docx,.txt,.zip,.rar"
            multiple
          />
          
          {/* Message Input Container */}
          <div className="flex-1 relative">
            <div className="bg-white rounded-2xl border border-gray-300 px-4 py-2 min-h-[40px] flex items-center">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="w-full outline-none text-sm resize-none bg-transparent"
                style={{ minHeight: '24px' }}
              />
              
              {/* Emoji Button */}
              <button className="ml-2 p-1 text-gray-500 hover:text-gray-700 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-8-6h8m-8-4h8" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Send Button */}
          <button 
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className={`p-2 rounded-full transition-all duration-200 ${
              newMessage.trim()
                ? 'bg-green-500 text-white hover:bg-green-600 transform hover:scale-105' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParentChannelChat;
